import React, { useEffect, useState } from "react";
import AppHeader from "../components/AppHeader";
import CanvasNotice from "../components/CanvasNotice";
import ToastHost from "../components/ToastHost";
// import ToolHeader from "../components/ToolHeader";
import { ConfigProvider } from "antd";
import { designerThemeDark, designerThemeLight } from "../theme";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { themeState } from "../lib/atom";
import { canvasNoticeState } from "../ThreeJs/ReactStates/states";
import MapTileButton from "../components/DropDown";
import { clearUndoStack } from "../ThreeJs/Global/UndoManager";
import {
  downloadFacetReport,
  downloadShadeReport,
} from "../helpers/reportActions";
import { useParams } from "react-router-dom";

// Layout component that wraps the entire designer UI with headers, theme support, and tool controls
export const DesignerLayout = ({
  children,                // Main content passed into the layout
  rightPanel,              // Right-side panel content (segments, obstacles, etc.)
  rightPanelToggle,        // Right-side panel toggle content (segments/trees)
  type,                    // Controls the visibility of the layout container
  currentProject,          // Current project metadata
  handleUpdatePolygon,     // Callback to trigger polygon updates
  handleGenerateDxf,       // Callback to generate a DXF file
  updateDrawingMode,       // Callback to change drawing mode
  currentDrawingMode,      // Current drawing mode (e.g., select, draw, edit)
  arePanelsOK,             // Boolean to enable/disable UI panels depending on validation
  setCurrentSegment,
  currentSegment,
  canUndo,
  currentDesign,
  onDesignRefresh,
  setShadingValues,
  shadingValues,
  setRemovedSections,
  panelList
}) => {

  // Read/write Recoil state for the theme (light/dark)
  const theme = useRecoilValue(themeState);
  const canvasNotice = useRecoilValue(canvasNoticeState);
  const setCanvasNotice = useSetRecoilState(canvasNoticeState);
  const [hasExternalUnsavedChanges, setHasExternalUnsavedChanges] = useState(false);
  const saveDisabled = !canUndo && !hasExternalUnsavedChanges;
  const { token, designID } = useParams();

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleExternalChange = () => {
      setHasExternalUnsavedChanges(true);
    };

    window.addEventListener("DESIGN_UNSAVED_CHANGE", handleExternalChange);
    return () => {
      window.removeEventListener("DESIGN_UNSAVED_CHANGE", handleExternalChange);
    };
  }, []);

  useEffect(() => {
    window.__setCanvasNotice = setCanvasNotice;
    return () => {
      if (window.__setCanvasNotice === setCanvasNotice) delete window.__setCanvasNotice;
    };
  }, [setCanvasNotice]);

  const handleScreenshot = () => {
    if (window.captureScreenshot) {
      window.captureScreenshot();
    } else {
      console.error("captureScreenshot function is not available");
    }
  };

  const handleSaveConfirm = async () => {
    await handleUpdatePolygon();
    clearUndoStack();
    setHasExternalUnsavedChanges(false);
  };

  const handleDownloadShadeReport = (options = {}) => {
    return downloadShadeReport(currentDesign, {
      token,
      designId: designID,
      ...options,
    });
  };

  const handleDownloadFacetReport = (options = {}) => {
    return downloadFacetReport(currentDesign, {
      token,
      designId: designID,
      ...options,
    });
  };

  return (
    // Container div controlling visibility and preventing scroll overflow
    <div
      className="designer-shell"
      style={{
        visibility: type,
        pointerEvents: type === "hidden" ? "none" : "auto",
        overflow: "hidden",
        height: "100vh",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        position: "fixed",
        inset: 0,
      }}
    >

      {/* Ant Design's theme context based on current theme */}
      <ConfigProvider theme={theme ? designerThemeDark : designerThemeLight}>

        {/* Main application header with theme switch and project info */}
        <AppHeader
            theme={theme}
            currentProject={currentProject}
            onScreenshot={handleScreenshot}
            onDownloadShadeReport={handleDownloadShadeReport}
            onDownloadFacetReport={handleDownloadFacetReport}
            onGenerateDxf={handleGenerateDxf}
            onSave={handleSaveConfirm}
            saveDisabled={saveDisabled}
            panelList={panelList}
            currentDesign={currentDesign}
            onDesignRefresh={onDesignRefresh}
            updateDrawingMode={updateDrawingMode}
            currentDrawingMode={currentDrawingMode}
        />

        {/* Toolbar below the header for drawing tools, polygon updates, and DXF export */}
        <div className="designer-body">
          {/*
          <ToolHeader
              theme={theme}
              updateDrawingMode={updateDrawingMode}
              currentDrawingMode={currentDrawingMode}
              setCurrentSegment={setCurrentSegment}
              currentSegment={currentSegment}
              currentDesign={currentDesign}
              setShadingValues={setShadingValues}
              shadingValues={shadingValues}
              setRemovedSections={setRemovedSections}
          />
          */}

          {/* Main content container that fills remaining viewport height */}
          <div className="designer-canvas">
            {/* Bottom-right button for selecting map tiles (within canvas area) */}
            <div className="map-tile-button">
              <MapTileButton currentDesign={currentDesign} />
            </div>

            <CanvasNotice notice={canvasNotice} theme={theme} />

            {/* On-brand gradient toast stack (top-center) */}
            <ToastHost />

            {/* Render passed children (likely the design canvas or map editor) */}
            {children}
          </div>

          <div
            className="designer-right-panel"
            style={{
              borderLeft: "none",
            }}
          >
            {rightPanelToggle ? (
              <div className="designer-right-panel-toggle">{rightPanelToggle}</div>
            ) : null}
            <div className="designer-right-panel-content">{rightPanel}</div>
          </div>
        </div>
      </ConfigProvider>
    </div>
  );
};
